import React from 'react';
import { styled } from '@mui/material/styles';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { OrderedQuestionEntry } from './workspaceDerivations';
import { ACCENT_BLUE_DARK } from './qcTheme';

// The `#` question-link dropdown: floats over the composer textarea, sits inline when editing a card.
const Menu = styled('div', {
  shouldForwardProp: prop => prop !== 'floating',
})<{ floating: boolean }>(({ floating }) => ({
  background: '#fff',
  border: floating ? 'none' : '1px solid rgba(0,0,0,0.12)',
  borderRadius: '6px',
  boxShadow: floating
    ? '0 5px 5px -3px rgba(0,0,0,0.2),0 8px 10px 1px rgba(0,0,0,0.14),0 3px 14px 2px rgba(0,0,0,0.12)'
    : '0 4px 12px rgba(0,0,0,0.12)',
  padding: '4px 0',
  maxHeight: floating ? '210px' : '190px',
  overflow: 'auto',
  ...(floating
    ? { position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 30 }
    : { marginTop: '6px' }),
}));

const MenuCaption = styled('div')({
  padding: '7px 14px 5px',
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: 'rgba(0,0,0,0.5)',
});

const MenuRow = styled('button')({
  display: 'grid',
  gridTemplateColumns: 'auto 22px 1fr',
  columnGap: '10px',
  alignItems: 'center',
  width: '100%',
  padding: '8px 14px',
  border: 'none',
  background: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: '#f3f5fb',
  },
});

const RowCode = styled('span')({
  fontSize: '0.75rem',
  fontWeight: 700,
  color: ACCENT_BLUE_DARK,
  whiteSpace: 'nowrap',
});

const RosetteSlot = styled('span')({
  display: 'flex',
  justifyContent: 'center',
});

const RowTitle = styled('span')({
  fontSize: '0.82rem',
  color: 'rgba(0,0,0,0.75)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export interface QuestionMentionMenuProps {
  matches: OrderedQuestionEntry[];
  floating?: boolean;
  onPick: (shortCode: string) => void;
}

export default function QuestionMentionMenu({ matches, floating = false, onPick }: QuestionMentionMenuProps): JSX.Element | null {
  if (!matches.length) return null;
  return (
    <Menu floating={floating}>
      <MenuCaption>Link a question</MenuCaption>
      {matches.map(({ question }) => (
        <MenuRow key={question.id} type='button' onClick={() => onPick(question.shortCode || '')}>
          <RowCode>{question.shortCode}</RowCode>
          <RosetteSlot>
            {question.isBestPractice && (
              <WorkspacePremiumIcon titleAccess='Best Practice question' sx={{ fontSize: 15, color: '#D467E4' }} />
            )}
          </RosetteSlot>
          <RowTitle>{question.title}</RowTitle>
        </MenuRow>
      ))}
    </Menu>
  );
}
